/**
 * Utility function to clear authentication cache and force recheck
 */
export const clearCacheAndRecheck = async (): Promise<boolean> => {
  try {
    // Clear localStorage auth tokens
    for (const key in localStorage) {
      if (key.startsWith("supabase.auth.")) {
        localStorage.removeItem(key);
      }
    }

    // Clear sessionStorage cache
    sessionStorage.removeItem("auth_session_cache");
    sessionStorage.removeItem("superadmin_status_cache");
    sessionStorage.removeItem("superadmin_cache_timestamp");
    sessionStorage.setItem("force_bypass_superadmin_cache", "true");

    // Return true to indicate cache was cleared successfully
    return true;
  } catch (error) {
    console.error("Error clearing cache:", error);
    return false;
  }
};

/**
 * Comprehensive logout function that clears all authentication-related storage
 * and handles cross-tab synchronization
 */
export const performComprehensiveLogout = async (): Promise<void> => {
  try {
    // 1. Sign out from Supabase
    const { supabase } = await import("@/integrations/supabase/client");
    await supabase.auth.signOut();

    // 2. Clear all authentication-related localStorage items
    const authKeysToRemove = [
      "SUPABASE_SESSION",
      "superadmin_status_cache",
      "superadmin_cache_timestamp",
      "userSessionId",
      "anonymous_session_id",
      "anon_id",
    ];

    authKeysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });

    // 3. Clear all Supabase auth keys from localStorage
    for (const key in localStorage) {
      if (key.startsWith("supabase.auth.")) {
        localStorage.removeItem(key);
      }
    }

    // 4. Clear sessionStorage items
    const sessionKeysToRemove = [
      "recoveryToken",
      "recoveryRefreshToken",
      "force_bypass_superadmin_cache",
      "auth_session_cache",
      "superadmin_status_cache",
      "superadmin_cache_timestamp",
      "dashboard_active_section",
    ];

    sessionKeysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
    });

    // 5. Clear profile-related caches
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("profiles-")) {
        sessionStorage.removeItem(key);
      }
    }

    // 6. Clear form persistence data
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.includes("_form_") || key.includes("_section_"))) {
        sessionStorage.removeItem(key);
      }
    }

    // 7. Dispatch logout event for cross-tab synchronization
    window.dispatchEvent(
      new CustomEvent("comprehensive-logout", {
        detail: { timestamp: Date.now() },
      })
    );
  } catch (error) {
    console.error("Error during comprehensive logout:", error);
    throw error;
  }
};

/**
 * Check if user is properly logged out by verifying no auth data remains
 */
export const verifyLogoutStatus = (): boolean => {
  try {
    // Check for any remaining auth data
    const hasSupabaseSession = localStorage.getItem("SUPABASE_SESSION");
    const hasSuperadminCache = localStorage.getItem("superadmin_status_cache");
    const hasAuthTokens = Object.keys(localStorage).some((key) =>
      key.startsWith("supabase.auth.")
    );

    return !hasSupabaseSession && !hasSuperadminCache && !hasAuthTokens;
  } catch (error) {
    console.error("Error verifying logout status:", error);
    return false;
  }
};
